import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Calculator, CheckCircle, Sun, ChevronRight, Loader2, Info, ExternalLink,
  Building2, User, Plus, Trash2, HelpCircle, X, Receipt,
} from 'lucide-react';
import { bicFromBelgianIban } from '../lib/belgianBanks';
import { ibanError, isValidBic } from '../../supabase/functions/_shared/sepa';
import { addressErrorCode } from '../lib/address';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { joinSteps } from '../lib/joinSteps';

const EDGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-questionnaire`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const EAN_PREFIX = '5414489';

type ParticipantType = 'particulier' | 'entreprise' | '';

type VatRegime = 'assujetti' | 'non_assujetti' | 'franchise' | '';
type InstallationRight = 'owner' | 'usage' | '';
type HousingType = 'maison' | 'appartement' | 'autre' | '';

/** Consommation annuelle proposée selon le logement. */
const HOUSING_DEFAULTS: Record<Exclude<HousingType, ''>, { label: string; kwh: number | null }> = {
  maison: { label: 'Maison', kwh: 4000 },
  appartement: { label: 'Appartement', kwh: 1500 },
  autre: { label: 'Je connais ma consommation', kwh: null },
};

interface ExtraMeter {
  ean_code: string;
  meter_address: string;
}

interface BaseFormData {
  participant_type: ParticipantType;
  role: 'producteur' | 'consommateur' | 'prosumer' | '';
  meter_type: 'intelligent' | 'mecanique' | '';
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  domicile_address: string;
  different_billing_address: boolean;
  billing_address: string;
  ean_code: string;
  meter_address_different: boolean;
  supply_address: string;
  extra_meters: ExtraMeter[];
  vat_regime: VatRegime;
  iban: string;
  account_holder: string;
  /** Particulier consommateur : domiciliation SEPA obligatoire (BIC et accord). */
  bic: string;
  sepa_consent: boolean;
  installed_power_kwc: string;
  installation_right: InstallationRight;
  /** Particulier consommateur ou prosumer : estimation des économies envoyée avec le contrat. */
  estimate_requested: boolean;
  housing_type: HousingType;
  annual_consumption_kwh: string;
  annual_cost_eur: string;
}

interface ParticulierFormData extends BaseFormData {
  participant_type: 'particulier';
  birth_date: string;
}

interface EntrepriseFormData extends BaseFormData {
  participant_type: 'entreprise';
  company_number: string;
  company_name: string;
  legal_form: string;
  representative_title: string;
  different_billing_email: boolean;
  billing_email: string;
}

// Ensemble de tous les champs (l'intersection directe des deux types donnait « never » à cause de participant_type).
type AllFields = Omit<ParticulierFormData, 'participant_type'> & Omit<EntrepriseFormData, 'participant_type'> & { participant_type: ParticipantType };

type Status = 'intro' | 'form' | 'submitting' | 'success';

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  hint?: string;
}

function Field({ label, value, onChange, error, placeholder, type = 'text', required = true, hint }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-800 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={ev => onChange(ev.target.value)}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
          error
            ? 'border-red-300 focus:ring-red-200 bg-red-50'
            : 'border-neutral-200 focus:ring-amber-200 focus:border-amber-400 bg-white'
        }`}
      />
      {hint && !error && <p className="text-xs text-neutral-400 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function SmartMeterTooltip() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Fermeture au clic (ou toucher) en dehors de l'encadré et à la touche Échap.
  useEffect(() => {
    if (!open) return;
    const onPointer = (ev: PointerEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex items-center ml-1.5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center hover:bg-amber-200 transition-colors focus:outline-none"
        aria-label={t('join.meter.tooltipAria')}
      >
        <Info className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] bg-neutral-900 text-white text-xs rounded-xl p-3 shadow-xl z-50">
          <p className="font-semibold mb-1.5 text-amber-300">{t('join.meter.tooltipTitle')}</p>
          <p className="text-neutral-300 leading-relaxed mb-2">
            {t('join.meter.tooltipText')}
          </p>
          <figure className="mb-2">
            <img src="/images/compteur-intelligent-exemple.jpg" alt={t('join.meter.exampleAlt')} loading="lazy"
              className="w-full max-h-64 object-contain rounded-lg bg-white" />
            <figcaption className="mt-1 text-[11px] text-neutral-400">{t('join.meter.exampleCaption')}</figcaption>
          </figure>
          <a
            href="https://www.sibelga.be/fr/raccordements-compteurs/compteurs-intelligents/les-details-du-compteur-intelligent"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-medium"
          >
            {t('join.meter.learnMore')} <ExternalLink className="w-3 h-3" />
          </a>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-2 h-2 bg-neutral-900 rotate-45 mb-[-4px]" />
        </div>
      )}
    </div>
  );
}

function SectionHeader({ num, label }: { num: number; label: string }) {
  return (
    <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4 flex items-center gap-2">
      <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">{num}</span>
      {label}
    </h3>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 text-sm font-medium transition-colors ${checked ? 'text-amber-700' : 'text-neutral-500 hover:text-neutral-700'}`}
    >
      <div className={`w-9 h-5 rounded-full transition-colors relative ${checked ? 'bg-amber-400' : 'bg-neutral-200'}`}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
      {label}
    </button>
  );
}

const emptyForm: AllFields = {
  participant_type: '' as any,
  role: '',
  meter_type: '',
  email: '',
  first_name: '',
  last_name: '',
  domicile_address: '',
  different_billing_address: false,
  billing_address: '',
  company_number: '',
  different_billing_email: false,
  billing_email: '',
  ean_code: EAN_PREFIX,
  phone: '',
  birth_date: '',
  company_name: '',
  legal_form: '',
  representative_title: '',
  meter_address_different: false,
  supply_address: '',
  extra_meters: [],
  vat_regime: '',
  iban: '',
  account_holder: '',
  bic: '',
  sepa_consent: false,
  installed_power_kwc: '',
  installation_right: '',
  estimate_requested: false,
  housing_type: '',
  annual_consumption_kwh: '',
  annual_cost_eur: '',
};

const INSTALLATION_RIGHT_OPTIONS: { value: Exclude<InstallationRight, ''>; key: string }[] = [
  { value: 'owner', key: 'owner' },
  { value: 'usage', key: 'usage' },
];

const VAT_OPTIONS: { value: Exclude<VatRegime, ''>; key: string }[] = [
  { value: 'assujetti', key: 'vatAssujetti' },
  { value: 'non_assujetti', key: 'vatNon' },
  { value: 'franchise', key: 'vatFranchise' },
];

/* Écran d'accueil : le parcours d'adhésion, avant le formulaire. */
function JoinIntro({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation();
  const JOIN_STEPS = joinSteps(t, 'tous');
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-neutral-50 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sun className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{t('join.intro.badge')}</span>
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-3">{t('join.intro.title')}</h1>
          <p className="text-neutral-500 text-base">{t('join.intro.subtitle')}</p>
        </div>
        <ol className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-6 sm:p-8 space-y-6">
          {JOIN_STEPS.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F8BD3D' }}>
                  <span className="text-white font-bold">{i + 1}</span>
                </div>
                {i < JOIN_STEPS.length - 1 && <div className="w-px flex-1 bg-amber-200 mt-2" />}
              </div>
              <div className="pb-1">
                <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900 mb-1">
                  <step.icon className="w-4 h-4 text-amber-500" />{step.title}
                </h2>
                {step.lines.map(line => <p key={line} className="text-sm text-neutral-600 leading-relaxed mb-1">{line}</p>)}
                {step.list && (
                  <ul className="mt-1 space-y-0.5 text-sm text-neutral-600 list-disc pl-5">
                    {step.list.map(item => <li key={item}>{item}</li>)}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-8 text-center space-y-3">
          <p className="mx-auto max-w-md flex items-start gap-2 text-left text-sm text-neutral-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
            <Receipt className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <span>{t('join.intro.prepare')}</span>
          </p>
          <button
            type="button"
            onClick={onStart}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
            style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
          >
            {t('join.intro.start')}<ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-neutral-400">
            {t('join.intro.question')} <Link to="/contact" className="underline hover:text-neutral-600">{t('join.intro.contact')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function NousRejoindre() {
  const { t, i18n } = useTranslation();
  const e_ = (key: string, vars?: Record<string, unknown>) => t(`join.errors.${key}`, vars);
  const numberLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').startsWith('nl') ? 'nl-BE' : (i18n.resolvedLanguage ?? i18n.language ?? 'fr').startsWith('en') ? 'en-GB' : 'fr-BE';
  const [status, setStatus] = useState<Status>('intro');
  /** Aide : exemple de facture avec l'emplacement du code EAN. */
  const [showEanHelp, setShowEanHelp] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Lien envoyé depuis le CRM : l'adresse du lead est pré-remplie (?email=…), pour compléter ce lead.
  const [form, setForm] = useState<AllFields>(() => {
    try {
      const email = new URLSearchParams(window.location.search).get('email')?.trim() ?? '';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? { ...emptyForm, email } : emptyForm;
    } catch {
      return emptyForm;
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (key: keyof AllFields, value: string | boolean) =>
    setForm(f => ({ ...f, [key]: value }));

  /** Estimation des économies : proposée aux particuliers consommateurs et prosumers. */
  const showEstimate = form.participant_type === 'particulier' && (form.role === 'consommateur' || form.role === 'prosumer');
  const chooseHousing = (type: Exclude<HousingType, ''>) =>
    setForm(f => {
      const defaults = Object.values(HOUSING_DEFAULTS).map(h => String(h.kwh ?? ''));
      // La consommation proposée remplace une valeur vide ou une autre valeur proposée, jamais une saisie.
      const replace = !f.annual_consumption_kwh.trim() || defaults.includes(f.annual_consumption_kwh.trim());
      const kwh = HOUSING_DEFAULTS[type].kwh;
      return { ...f, housing_type: type, annual_consumption_kwh: replace ? (kwh ? String(kwh) : '') : f.annual_consumption_kwh };
    });

  /** IBAN saisi : BIC proposé pour un compte belge (s'il est vide ou avait été proposé automatiquement). */
  const setIban = (value: string) =>
    setForm(f => {
      const before = bicFromBelgianIban(f.iban);
      const proposed = bicFromBelgianIban(value);
      return { ...f, iban: value, bic: proposed && (!f.bic.trim() || f.bic === before) ? proposed : f.bic };
    });

  const setExtraMeter = (index: number, patch: Partial<ExtraMeter>) =>
    setForm(f => ({ ...f, extra_meters: f.extra_meters.map((m, i) => (i === index ? { ...m, ...patch } : m)) }));
  const addExtraMeter = () =>
    setForm(f => ({ ...f, extra_meters: [...f.extra_meters, { ean_code: EAN_PREFIX, meter_address: '' }] }));
  const removeExtraMeter = (index: number) =>
    setForm(f => ({ ...f, extra_meters: f.extra_meters.filter((_, i) => i !== index) }));

  const onEanChange = (v: string, apply: (value: string) => void) => {
    if (v.startsWith(EAN_PREFIX) || EAN_PREFIX.startsWith(v)) apply(v);
    else if (v.length < EAN_PREFIX.length) apply(EAN_PREFIX);
  };

  const validateEan = (ean: string): string | undefined => {
    if (!ean.trim()) return e_('required');
    if (!ean.startsWith(EAN_PREFIX)) return e_('eanPrefix', { prefix: EAN_PREFIX });
    if (ean.replace(/\D/g, '').length !== 18) return e_('eanLength');
    return undefined;
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    const producerLike = form.role === 'producteur' || form.role === 'prosumer';

    if (!form.participant_type) e.participant_type = e_('selectType');
    if (!form.role) e.role = e_('selectRole');
    if (!form.meter_type) e.meter_type = e_('selectMeter');
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = e_('email');
    // Téléphone facultatif, contrôlé s'il est donné.
    if (form.phone.trim() && form.phone.replace(/\D/g, '').length < 8) e.phone = e_('phone');
    if (!form.first_name.trim()) e.first_name = e_('required');
    if (!form.last_name.trim()) e.last_name = e_('required');
    const domicileErr = addressErrorCode(form.domicile_address);
    if (domicileErr) e.domicile_address = e_(domicileErr);
    if (form.different_billing_address) {
      const billingErr = addressErrorCode(form.billing_address);
      if (billingErr) e.billing_address = e_(billingErr);
    }

    if (form.participant_type === 'particulier') {
      if (!form.birth_date) e.birth_date = e_('required');
    }

    if (form.participant_type === 'entreprise') {
      if (!form.company_name.trim()) e.company_name = e_('required');
      if (!form.legal_form.trim()) e.legal_form = e_('required');
      if (!form.representative_title.trim()) e.representative_title = e_('required');
      if (!form.company_number.trim()) e.company_number = e_('required');
      if (form.different_billing_email) {
        if (!form.billing_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.billing_email)) {
          e.billing_email = e_('email');
        }
      }
    }

    if (producerLike) {
      // Personne physique : non assujettie (seul régime de la convention PP). Le régime n'est demandé qu'aux sociétés.
      if (form.participant_type === 'entreprise' && !form.vat_regime) e.vat_regime = e_('vat');
      const kwc = Number(form.installed_power_kwc.replace(',', '.'));
      if (!form.installed_power_kwc.trim() || !Number.isFinite(kwc) || kwc <= 0) e.installed_power_kwc = e_('power');
      if (!form.installation_right) e.installation_right = e_('right');
      if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(form.iban.replace(/\s/g, '').toUpperCase())) e.iban = e_('iban');
      if (!form.account_holder.trim()) e.account_holder = e_('required');
    }

    // Particulier consommateur : domiciliation SEPA obligatoire.
    if (form.participant_type === 'particulier' && form.role === 'consommateur') {
      const ibanErr = ibanError(form.iban);
      if (ibanErr) e.iban = ibanErr === 'IBAN manquant' ? e_('required') : ibanErr.includes('clé de contrôle') ? e_('ibanChecksum') : e_('iban');
      if (!form.account_holder.trim()) e.account_holder = e_('required');
      if (!isValidBic(form.bic)) e.bic = form.bic.trim() ? e_('bic') : e_('required');
      if (!form.sepa_consent) e.sepa_consent = e_('sepa');
    }

    // Estimation des économies demandée : consommation annuelle obligatoire, montant payé facultatif.
    if (showEstimate && form.estimate_requested) {
      const kwh = Number(form.annual_consumption_kwh.replace(/\s/g, '').replace(',', '.'));
      if (!form.annual_consumption_kwh.trim() || !Number.isFinite(kwh) || kwh < 100 || kwh > 100000) {
        e.annual_consumption_kwh = e_('consumption');
      }
      const cost = Number(form.annual_cost_eur.replace(/\s/g, '').replace(',', '.'));
      if (form.annual_cost_eur.trim() && (!Number.isFinite(cost) || cost <= 0 || cost > 100000)) e.annual_cost_eur = e_('cost');
    }

    const eanError = validateEan(form.ean_code);
    if (eanError) e.ean_code = eanError;
    if (form.meter_address_different) {
      const supplyErr = addressErrorCode(form.supply_address);
      if (supplyErr) e.supply_address = e_(supplyErr);
    }

    const allEans = [form.ean_code, ...form.extra_meters.map(m => m.ean_code)].map(v => v.replace(/\D/g, ''));
    form.extra_meters.forEach((m, i) => {
      const err = validateEan(m.ean_code) ?? (allEans.indexOf(allEans[i + 1]) !== i + 1 ? e_('eanDuplicate') : undefined);
      if (err) e[`extra_meters.${i}.ean_code`] = err;
      const meterErr = addressErrorCode(m.meter_address);
      if (meterErr) e[`extra_meters.${i}.meter_address`] = e_(meterErr);
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setSubmitError(t('join.submit.fixErrors'));
      return;
    }
    setSubmitError(null);
    setStatus('submitting');

    const billingAddr = form.different_billing_address ? form.billing_address.trim() : form.domicile_address.trim();
    const sepaConsumer = form.participant_type === 'particulier' && form.role === 'consommateur';
    const billingEmail = (form.participant_type === 'entreprise' && form.different_billing_email) ? form.billing_email.trim() : null;

    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ANON_KEY}`,
          'apikey': ANON_KEY,
        },
        body: JSON.stringify({
          participant_type: form.participant_type,
          role: form.role,
          meter_type: form.meter_type,
          email: form.email.trim(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          address: billingAddr,
          ean_code: form.ean_code.trim(),
          domicile_address: form.domicile_address.trim(),
          billing_address: form.different_billing_address ? form.billing_address.trim() : null,
          company_number: form.participant_type === 'entreprise' ? form.company_number.trim() : null,
          billing_email: billingEmail,
          supply_address: form.meter_address_different ? form.supply_address.trim() : null,
          phone: form.phone.trim() || null,
          birth_date: form.participant_type === 'particulier' && form.birth_date ? form.birth_date : null,
          company_name: form.participant_type === 'entreprise' ? form.company_name.trim() : null,
          legal_form: form.participant_type === 'entreprise' ? form.legal_form.trim() : null,
          representative: form.participant_type === 'entreprise' ? `${form.first_name.trim()} ${form.last_name.trim()}` : null,
          representative_title: form.participant_type === 'entreprise' ? form.representative_title.trim() || null : null,
          vat_regime: (form.role === 'producteur' || form.role === 'prosumer') && form.participant_type === 'entreprise' ? form.vat_regime : null,
          installed_power_kwc: form.role === 'producteur' || form.role === 'prosumer' ? Number(form.installed_power_kwc.replace(',', '.')) : null,
          installation_right: form.role === 'producteur' || form.role === 'prosumer' ? form.installation_right || null : null,
          iban: form.role === 'producteur' || form.role === 'prosumer' || sepaConsumer ? form.iban.replace(/\s/g, '').toUpperCase() : null,
          account_holder: form.role === 'producteur' || form.role === 'prosumer' || sepaConsumer ? form.account_holder.trim() : null,
          bic: sepaConsumer ? form.bic.replace(/\s/g, '').toUpperCase() : null,
          sepa_consent: sepaConsumer ? form.sepa_consent : null,
          ...(showEstimate && form.estimate_requested ? {
            estimate_requested: true,
            housing_type: form.housing_type || null,
            annual_consumption_kwh: Number(form.annual_consumption_kwh.replace(/\s/g, '').replace(',', '.')),
            annual_cost_eur: form.annual_cost_eur.trim() ? Number(form.annual_cost_eur.replace(/\s/g, '').replace(',', '.')) : null,
          } : {}),
          meters: [
            {
              ean_code: form.ean_code.trim(),
              meter_address: form.meter_address_different ? form.supply_address.trim() : form.domicile_address.trim(),
              meter_type: form.meter_type || null,
            },
            ...form.extra_meters.map(m => ({ ean_code: m.ean_code.trim(), meter_address: m.meter_address.trim(), meter_type: form.meter_type || null })),
          ],
        }),
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => null) as { error?: string } | null;
        setSubmitError(t('join.submit.failed', { detail: payload?.error ? ` : ${payload.error}` : '' }));
        setStatus('form');
        return;
      }

      setStatus('success');
    } catch {
      setSubmitError(t('join.submit.network'));
      setStatus('form');
    }
  };

  if (status === 'intro') {
    return <JoinIntro onStart={() => { setStatus('form'); window.scrollTo(0, 0); }} />;
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-neutral-50 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-neutral-100 p-10 text-center">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">{t('join.success.title')}</h2>
          <p className="text-neutral-600 text-sm leading-relaxed">
            {t('join.success.line1')}<br />
            {t('join.success.line2')}
          </p>
          <div className="mt-6 pt-6 border-t border-neutral-100">
            <p className="text-xs text-neutral-400">Sun Is Up — <a href="mailto:info@sunisup.be" className="hover:underline">info@sunisup.be</a></p>
          </div>
        </div>
      </div>
    );
  }

  const isParticulier = form.participant_type === 'particulier';
  const isEntreprise = form.participant_type === 'entreprise';
  const typeSelected = isParticulier || isEntreprise;

  let sectionNum = 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-neutral-50 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sun className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{t('join.form.badge')}</span>
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-3">{t('join.form.title')}</h1>
          <p className="text-neutral-500 text-base">
            {t('join.form.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8 space-y-6">

            {/* Section 1 — Type de participant */}
            <div>
              <SectionHeader num={sectionNum++} label={t('join.sections.youAre')} />
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => set('participant_type', 'particulier')}
                  className={`py-4 px-4 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-2 ${
                    isParticulier
                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <User className={`w-5 h-5 ${isParticulier ? 'text-amber-600' : 'text-neutral-400'}`} />
                  {t('join.types.particulier')}
                </button>
                <button
                  type="button"
                  onClick={() => set('participant_type', 'entreprise')}
                  className={`py-4 px-4 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-2 ${
                    isEntreprise
                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <Building2 className={`w-5 h-5 ${isEntreprise ? 'text-amber-600' : 'text-neutral-400'}`} />
                  {t('join.types.entreprise')}
                </button>
              </div>
              {errors.participant_type && <p className="text-xs text-red-500 mt-1">{errors.participant_type}</p>}
            </div>

            {/* Rest of form — only shown once type is selected */}
            {typeSelected && (
              <>
                {/* Section — Profil */}
                <div className="border-t border-neutral-100 pt-6">
                  <SectionHeader num={sectionNum++} label={t('join.sections.profile')} />
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-800 mb-1.5">
                        {t('join.role.label')} <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {(['producteur', 'consommateur', 'prosumer'] as const).map(r => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => set('role', r)}
                            className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                              form.role === r
                                ? 'border-amber-400 bg-amber-50 text-amber-800'
                                : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                            }`}
                          >
                            {t(`join.role.${r}`)}
                          </button>
                        ))}
                      </div>
                      {form.role === 'prosumer' && (
                        <p className="text-xs text-neutral-400 mt-1.5">{t('join.role.prosumerHint')}</p>
                      )}
                      {errors.role && <p className="text-xs text-red-500 mt-1">{errors.role}</p>}
                    </div>

                    <div>
                      <div className="flex items-center mb-1.5">
                        <label className="text-sm font-medium text-neutral-800">{t('join.meter.label')}</label>
                        <SmartMeterTooltip />
                        <span className="text-red-500 ml-0.5 text-sm">*</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {(['intelligent', 'mecanique'] as const).map(m => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => set('meter_type', m)}
                            className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                              form.meter_type === m
                                ? 'border-amber-400 bg-amber-50 text-amber-800'
                                : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                            }`}
                          >
                            {t(`join.meter.${m}`)}
                          </button>
                        ))}
                      </div>
                      {errors.meter_type && <p className="text-xs text-red-500 mt-1">{errors.meter_type}</p>}

                      {form.meter_type === 'mecanique' && (
                        <div className="mt-3 p-4 rounded-xl bg-green-50 border border-green-200">
                          <p className="text-sm font-semibold text-green-800 mb-1">{t('join.meter.goodNews')}</p>
                          <p className="text-sm text-green-700 leading-relaxed">
                            {t('join.meter.replaceFree')}
                          </p>
                          <a
                            href="https://sibelga.be/fr/raccordements-compteurs/mon-raccordement/demande-de-travaux/demande/electricite-remplacement-par-compteur-intelligent"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-green-700 hover:text-green-800 underline underline-offset-2"
                          >
                            {t('join.meter.sibelgaLink')}
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section — Contact */}
                <div className="border-t border-neutral-100 pt-6">
                  <SectionHeader num={sectionNum++} label={t('join.sections.contact')} />
                  <div className="space-y-4">
                    <Field
                      label={t('join.contact.email')}
                      value={form.email}
                      onChange={v => set('email', v)}
                      error={errors.email}
                      placeholder={t('join.contact.emailPh')}
                      type="email"
                    />
                    <Field
                      label={t('join.contact.phone')}
                      value={form.phone}
                      onChange={v => set('phone', v)}
                      error={errors.phone}
                      placeholder="+32 470 12 34 56"
                      type="tel"
                      required={false}
                    />
                    {isEntreprise && (
                      <div className="pt-1">
                        <Toggle
                          checked={form.different_billing_email}
                          onChange={v => set('different_billing_email', v)}
                          label={t('join.contact.diffBillingEmail')}
                        />
                        {form.different_billing_email && (
                          <div className="mt-3">
                            <Field
                              label={t('join.contact.billingEmail')}
                              value={form.billing_email}
                              onChange={v => set('billing_email', v)}
                              error={errors.billing_email}
                              placeholder={t('join.contact.billingEmailPh')}
                              type="email"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Section — Identité */}
                <div className="border-t border-neutral-100 pt-6">
                  <SectionHeader num={sectionNum++} label={t('join.sections.identity')} />
                  <div className="space-y-4">
                    {isEntreprise && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                          label={t('join.identity.companyName')}
                          value={form.company_name}
                          onChange={v => set('company_name', v)}
                          error={errors.company_name}
                          placeholder={t('join.identity.companyNamePh')}
                        />
                        <Field
                          label={t('join.identity.legalForm')}
                          value={form.legal_form}
                          onChange={v => set('legal_form', v)}
                          error={errors.legal_form}
                          placeholder={t('join.identity.legalFormPh')}
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        label={isEntreprise ? t('join.identity.repFirstName') : t('join.identity.firstName')}
                        value={form.first_name}
                        onChange={v => set('first_name', v)}
                        error={errors.first_name}
                        placeholder={t('join.identity.firstNamePh')}
                      />
                      <Field
                        label={isEntreprise ? t('join.identity.repLastName') : t('join.identity.lastName')}
                        value={form.last_name}
                        onChange={v => set('last_name', v)}
                        error={errors.last_name}
                        placeholder={t('join.identity.lastNamePh')}
                      />
                    </div>
                    {isParticulier && (
                      <Field
                        label={t('join.identity.birthDate')}
                        value={form.birth_date}
                        onChange={v => set('birth_date', v)}
                        error={errors.birth_date}
                        type="date"
                      />
                    )}
                    {isEntreprise && (
                      <Field
                        label={t('join.identity.repTitle')}
                        value={form.representative_title}
                        onChange={v => set('representative_title', v)}
                        error={errors.representative_title}
                        placeholder={t('join.identity.repTitlePh')}
                      />
                    )}
                    {isEntreprise && (
                      <Field
                        label={t('join.identity.bce')}
                        value={form.company_number}
                        onChange={v => set('company_number', v)}
                        error={errors.company_number}
                        placeholder="BE 0XXX.XXX.XXX"
                        hint={t('join.identity.bceHint')}
                      />
                    )}
                  </div>
                </div>

                {/* Section — Adresse */}
                <div className="border-t border-neutral-100 pt-6">
                  <SectionHeader num={sectionNum++} label={t('join.sections.address')} />
                  <div className="space-y-4">
                    <AddressAutocomplete
                      label={isEntreprise ? t('join.address.seat') : t('join.address.domicile')}
                      value={form.domicile_address}
                      onChange={v => set('domicile_address', v)}
                      error={errors.domicile_address}
                    />
                    <Toggle
                      checked={form.different_billing_address}
                      onChange={v => set('different_billing_address', v)}
                      label={t('join.address.diffBilling')}
                    />
                    {form.different_billing_address && (
                      <AddressAutocomplete
                        label={t('join.address.billing')}
                        value={form.billing_address}
                        onChange={v => set('billing_address', v)}
                        error={errors.billing_address}
                      />
                    )}
                  </div>
                </div>

                {/* Section — Installation de production (producteur et prosumer) */}
                {(form.role === 'producteur' || form.role === 'prosumer') && (
                  <div className="border-t border-neutral-100 pt-6">
                    <SectionHeader num={sectionNum++} label={t('join.sections.production')} />
                    <div className="space-y-4">
                      <Field
                        label={t('join.production.power')}
                        value={form.installed_power_kwc}
                        onChange={v => set('installed_power_kwc', v.replace(/[^\d.,]/g, ''))}
                        error={errors.installed_power_kwc}
                        placeholder="9,6"
                        hint={t('join.production.powerHint')}
                      />
                      <div>
                        <label className="block text-sm font-medium text-neutral-800 mb-1.5">
                          {t('join.production.right')} <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {INSTALLATION_RIGHT_OPTIONS.map(o => (
                            <button
                              key={o.value}
                              type="button"
                              onClick={() => set('installation_right', o.value)}
                              className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                                form.installation_right === o.value
                                  ? 'border-amber-400 bg-amber-50 text-amber-800'
                                  : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                              }`}
                            >
                              {t(`join.production.${o.key}`)}
                            </button>
                          ))}
                        </div>
                        {errors.installation_right && <p className="text-xs text-red-500 mt-1">{errors.installation_right}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Section — Coordonnées bancaires (producteur et prosumer) */}
                {(form.role === 'producteur' || form.role === 'prosumer') && (
                  <div className="border-t border-neutral-100 pt-6">
                    <SectionHeader num={sectionNum++} label={t('join.sections.bank')} />
                    <div className="space-y-4">
                      {isEntreprise ? (
                      <div>
                        <label className="block text-sm font-medium text-neutral-800 mb-1.5">
                          {t('join.bank.vat')} <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {VAT_OPTIONS.map(o => (
                            <button
                              key={o.value}
                              type="button"
                              onClick={() => set('vat_regime', o.value)}
                              className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                                form.vat_regime === o.value
                                  ? 'border-amber-400 bg-amber-50 text-amber-800'
                                  : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                              }`}
                            >
                              {t(`join.bank.${o.key}`)}
                            </button>
                          ))}
                        </div>
                        {errors.vat_regime && <p className="text-xs text-red-500 mt-1">{errors.vat_regime}</p>}
                      </div>
                      ) : (
                        <p className="text-xs text-neutral-500 leading-relaxed">
                          {t('join.bank.vatNote')}
                        </p>
                      )}
                      <Field
                        label="IBAN"
                        value={form.iban}
                        onChange={v => set('iban', v)}
                        error={errors.iban}
                        placeholder="BE68 5390 0754 7034"
                      />
                      <Field
                        label={t('join.bank.holder')}
                        value={form.account_holder}
                        onChange={v => set('account_holder', v)}
                        error={errors.account_holder}
                        placeholder={isEntreprise ? t('join.identity.companyNamePh') : t('join.bank.holderPh')}
                      />
                    </div>
                  </div>
                )}

                {/* Section — Coordonnées bancaires et domiciliation (particulier consommateur) */}
                {isParticulier && form.role === 'consommateur' && (
                  <div className="border-t border-neutral-100 pt-6">
                    <SectionHeader num={sectionNum++} label={t('join.sections.bank')} />
                    <div className="space-y-4">
                      <p className="text-sm text-neutral-700 leading-relaxed bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                        {t('join.bank.sepaInfo')}
                      </p>
                      <Field
                        label="IBAN"
                        value={form.iban}
                        onChange={setIban}
                        error={errors.iban}
                        placeholder="BE68 5390 0754 7034"
                      />
                      <Field
                        label={t('join.bank.holder')}
                        value={form.account_holder}
                        onChange={v => set('account_holder', v)}
                        error={errors.account_holder}
                        placeholder={t('join.bank.holderPh')}
                      />
                      <Field
                        label="BIC"
                        value={form.bic}
                        onChange={v => set('bic', v.toUpperCase())}
                        error={errors.bic}
                        placeholder="GEBABEBB"
                        hint={t('join.bank.bicHint')}
                      />
                      <div>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.sepa_consent}
                            onChange={ev => set('sepa_consent', ev.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-amber-500"
                          />
                          <span className="text-sm text-neutral-800">
                            {t('join.bank.sepaConsent')}<span className="text-red-500 ml-0.5">*</span>
                          </span>
                        </label>
                        {errors.sepa_consent && <p className="text-xs text-red-500 mt-1">{errors.sepa_consent}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Section — EAN */}
                <div className="border-t border-neutral-100 pt-6">
                  <SectionHeader num={sectionNum++} label={t('join.sections.meters')} />
                  <div className="space-y-4">
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                      <label className="block text-sm font-medium text-neutral-800">
                        {t('join.meters.ean')} <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <button type="button" onClick={() => setShowEanHelp(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline">
                        <HelpCircle className="w-3.5 h-3.5" />{t('join.meters.eanWhere')}
                      </button>
                    </div>
                    <input
                      type="text"
                      value={form.ean_code}
                      onChange={ev => onEanChange(ev.target.value, v => set('ean_code', v))}
                      maxLength={18}
                      placeholder={`${EAN_PREFIX}XXXXXXXXXXX`}
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 font-mono ${
                        errors.ean_code
                          ? 'border-red-300 focus:ring-red-200 bg-red-50'
                          : 'border-neutral-200 focus:ring-amber-200 focus:border-amber-400 bg-white'
                      }`}
                    />
                    <p className="text-xs text-neutral-400 mt-1.5">
                      {t('join.meters.eanHelpBefore')} <span className="font-mono font-semibold text-neutral-600">{EAN_PREFIX}</span> {t('join.meters.eanHelpAfter')}
                    </p>
                    {errors.ean_code && <p className="text-xs text-red-500 mt-1">{errors.ean_code}</p>}
                  </div>
                  {showEanHelp && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('join.meters.eanWhere')}>
                      <div className="absolute inset-0 bg-black/50" onClick={() => setShowEanHelp(false)} />
                      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-neutral-100">
                          <div>
                            <p className="text-base font-semibold text-neutral-900">{t('join.meters.eanWhere')}</p>
                            <p className="text-xs text-neutral-500 mt-0.5">{t('join.meters.eanWhereText')}</p>
                          </div>
                          <button type="button" onClick={() => setShowEanHelp(false)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400" aria-label={t('join.meters.eanWhereClose')}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="overflow-y-auto p-4 bg-neutral-50">
                          <img src="/images/facture-ean-exemple.jpg" alt={t('join.meters.eanWhereAlt')} className="w-full h-auto rounded-lg border border-neutral-200 bg-white" />
                        </div>
                      </div>
                    </div>
                  )}

                  <Toggle
                    checked={form.meter_address_different}
                    onChange={v => set('meter_address_different', v)}
                    label={isEntreprise ? t('join.meters.elsewhereSeat') : t('join.meters.elsewhereHome')}
                  />
                  {form.meter_address_different && (
                    <AddressAutocomplete
                      label={t('join.meters.meterAddress')}
                      value={form.supply_address}
                      onChange={v => set('supply_address', v)}
                      error={errors.supply_address}
                    />
                  )}

                  {form.extra_meters.map((m, i) => (
                    <div key={i} className="p-4 rounded-xl border border-neutral-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-neutral-800">{t('join.meters.meterN', { n: i + 2 })}</p>
                        <button type="button" onClick={() => removeExtraMeter(i)} className="flex items-center gap-1 text-xs text-neutral-500 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />{t('join.meters.remove')}
                        </button>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-800 mb-1.5">
                          {t('join.meters.ean')} <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          type="text"
                          value={m.ean_code}
                          onChange={ev => onEanChange(ev.target.value, v => setExtraMeter(i, { ean_code: v }))}
                          maxLength={18}
                          placeholder={`${EAN_PREFIX}XXXXXXXXXXX`}
                          className={`w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 font-mono ${
                            errors[`extra_meters.${i}.ean_code`]
                              ? 'border-red-300 focus:ring-red-200 bg-red-50'
                              : 'border-neutral-200 focus:ring-amber-200 focus:border-amber-400 bg-white'
                          }`}
                        />
                        {errors[`extra_meters.${i}.ean_code`] && <p className="text-xs text-red-500 mt-1">{errors[`extra_meters.${i}.ean_code`]}</p>}
                      </div>
                      <AddressAutocomplete
                        label={t('join.meters.meterAddress')}
                        value={m.meter_address}
                        onChange={v => setExtraMeter(i, { meter_address: v })}
                        error={errors[`extra_meters.${i}.meter_address`]}
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addExtraMeter}
                    className="flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-800"
                  >
                    <Plus className="w-4 h-4" />{t('join.meters.add')}
                  </button>
                  <p className="text-xs text-neutral-400 -mt-2">{t('join.meters.oneContract')}</p>
                  </div>
                </div>

                {/* Section — Estimation des économies (particulier consommateur ou prosumer) */}
                {showEstimate && (
                  <div className="border-t border-neutral-100 pt-6">
                    <SectionHeader num={sectionNum++} label={t('join.sections.estimate')} />
                    {!form.estimate_requested ? (
                      <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 space-y-3">
                        <p className="text-sm text-neutral-700 leading-relaxed">
                          {t('join.estimate.intro')}
                        </p>
                        <button
                          type="button"
                          onClick={() => { set('estimate_requested', true); if (!form.housing_type) chooseHousing('maison'); }}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:shadow-md"
                          style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                        >
                          <Calculator className="w-4 h-4" />{t('join.estimate.button')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-800 mb-1.5">{t('join.estimate.housing')}</label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {(Object.keys(HOUSING_DEFAULTS) as Exclude<HousingType, ''>[]).map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => chooseHousing(type)}
                                className={`py-3 px-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                  form.housing_type === type
                                    ? 'border-amber-400 bg-amber-50 text-amber-800'
                                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                                }`}
                              >
                                {t(`join.estimate.${type}`)}
                                {HOUSING_DEFAULTS[type].kwh && (
                                  <span className="block text-[11px] font-normal text-neutral-500">≈ {HOUSING_DEFAULTS[type].kwh!.toLocaleString(numberLocale)} {t('join.estimate.perYear')}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Field
                            label={t('join.estimate.consumption')}
                            value={form.annual_consumption_kwh}
                            onChange={v => set('annual_consumption_kwh', v.replace(/[^\d\s.,]/g, ''))}
                            error={errors.annual_consumption_kwh}
                            placeholder="4000"
                            hint={t('join.estimate.consumptionHint')}
                          />
                          <Field
                            label={t('join.estimate.cost')}
                            value={form.annual_cost_eur}
                            onChange={v => set('annual_cost_eur', v.replace(/[^\d\s.,]/g, ''))}
                            error={errors.annual_cost_eur}
                            placeholder="1200"
                            required={false}
                            hint={t('join.estimate.costHint')}
                          />
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-xs text-neutral-500">{t('join.estimate.byEmail')}</p>
                          <button type="button" onClick={() => set('estimate_requested', false)} className="text-xs text-neutral-500 hover:text-neutral-700 underline">
                            {t('join.estimate.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-2">
                  {submitError && (
                    <p className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{submitError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full py-4 rounded-xl font-semibold text-neutral-900 flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#F8BD3D' }}
                  >
                    {status === 'submitting' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />{t('join.submit.sending')}</>
                    ) : (
                      <>{t('join.submit.send')}<ChevronRight className="w-4 h-4" /></>
                    )}
                  </button>
                  <p className="text-xs text-neutral-400 text-center mt-3">{t('join.submit.privacy')}</p>
                </div>
              </>
            )}

          </form>
        </div>
      </div>
    </div>
  );
}
